/* eslint-disable class-methods-use-this */
import React from 'react';
import I18n from 'i18n-js';
import { pick, groupBy, uniq } from 'lodash';
import moment from 'moment';

import CoreLink from './core_link';
import CollapsingList from './collapsing_list';
import {
  core_apimessages_ProductSpec as ProductSpec,
} from '../rql';

interface IProps {
  specs: ProductSpec[];
  dateListed?: number;
  collapseList?: boolean;
}

interface IShortSpec {
  value?: string;
  path?: string;
  slug?: string;
  type?: string;
}

interface ISpecGroup {
  label: string;
  specs: IShortSpec[];
}

function Spec(props: { spec: IShortSpec }) {
  const { spec } = props;

  if (!spec.path || spec.path.includes('/marketplace?')) {
    return (
      <div key={spec.value}>
        {spec.value}
      </div>
    );
  }

  return (
    <CoreLink
      key={spec.value}
      to={spec.path}
    >
      {spec.value}
    </CoreLink>
  );
}

function ConditionRow(props: { spec: IShortSpec }) {
  const { spec } = props;
  const name = spec.value;
  let conditionText = I18n.t('commons.specList.conditionUsed', { name });

  if (spec.slug === 'brand-new') {
    conditionText = I18n.t('commons.specList.conditionNew', { name });
  }

  return (
    <>
      <td data-condition-label>
        {I18n.t('commons.specList.condition')}
      </td>
      <td data-condition>
        {conditionText}
        <div className="size-90">
          <span className="opacity-70 mr-space">
            {I18n.t(`commons.specList.conditions.${spec.slug}`)}
          </span>
          <CoreLink to={spec.path}>
            {I18n.t('commons.specList.learnMore')}
          </CoreLink>
        </div>
      </td>
    </>
  );
}

function SpecGroup(props: { group: ISpecGroup }) {
  const { group } = props;

  if (group.specs[0].type === 'CONDITION') {
    return <ConditionRow spec={group.specs[0]} />;
  }

  return (
    <React.Fragment key={group.label}>
      <td data-spec-label>
        {group.label}
      </td>
      <td data-spec-groups>
        <CollapsingList>
          {group.specs.map((spec) => {
            return <Spec key={spec.value} spec={spec} />;
          })}
        </CollapsingList>
      </td>
    </React.Fragment>
  );
}

export default class SpecList extends React.Component<IProps, null> {
  specGroups(): ISpecGroup[] {
    const labels = this.specsLabels();
    const specsByLabel = this.specsByLabel();

    return labels.map((label) => {
      const rawSpecs = specsByLabel[label];
      const specs = rawSpecs.map(this.shortSpec);

      return { label, specs };
    });
  }

  shortSpec(spec: ProductSpec): IShortSpec {
    return pick(spec, ['path', 'value', 'type', 'slug']);
  }

  specsByLabel() {
    return groupBy(this.props.specs, 'name');
  }

  specsLabels(): string[] {
    return uniq(this.props.specs.map(s => s.name));
  }

  renderListingAge(seconds: number): string {
    const day = moment.unix(seconds);
    const listingAge = moment(day, 'YYYYMMDD').from(this.now());

    return listingAge;
  }

  now(): moment.Moment {
    return moment();
  }

  render() {
    return (
      <table className="spec-list">
        <CollapsingList
          renderAsTableBody
          {...(!this.props.collapseList && { countThreshold: 99 })}
        >
          {
            this.props.dateListed &&
            <>
              <td>
                {I18n.t('commons.specList.listed')}
              </td>
              <td data-listing-age>
                {this.renderListingAge(this.props.dateListed)}
              </td>
            </>
          }
          {this.specGroups().map((group) => {
            return <SpecGroup key={group.label} group={group} />;
          })}
        </CollapsingList>
      </table>
    );
  }
}
